import React from 'react';
import { CheckCircle, Sparkles, Settings, BookOpen, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const StandardReminderModal = ({ 
  isOpen, 
  onClose 
}) => {
  const { tr } = useLanguage();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop with blur */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-all duration-300"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white/95 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 max-w-sm w-full mx-4 transform transition-all duration-300 scale-100 opacity-100">
        
        {/* Header with success animation */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-t-3xl p-6 relative overflow-hidden">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>

          {/* Success icon with animation */}
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              {/* Success glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl opacity-20 blur-xl animate-pulse" />
              {/* Sparkle effects */}
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center animate-bounce">
                <Sparkles className="w-2.5 h-2.5 text-white" />
              </div>
            </div>
          </div>

          <div className="text-center">
            <h2 className="text-lg font-bold text-gray-900 mb-2">
              {tr('Story Saved Successfully!', 'Histoire enregistrée !')}
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center space-y-4">
            <p className="text-gray-600 text-sm leading-relaxed">
              {tr('Your personalized story has been saved to', 'Votre histoire personnalisée a été enregistrée dans')} <strong>{tr('My Stories', 'Mes Histoires')}</strong>.
            </p>

            {/* Feature highlight */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-4 border border-indigo-100">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Settings className="w-4 h-4 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-900 mb-1">
                    💡 {tr('Standard Tip', 'Astuce Standard')}
                  </p>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {tr("Adjust any parameters above and click 'Create Another Story' to generate new variations instantly.", "Ajustez les paramètres ci-dessus et cliquez sur 'Créer une autre histoire' pour générer de nouvelles variations instantanément.")}
                  </p>
                </div>
              </div>
            </div>

            {/* Benefits list */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <div className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                {tr('Unlimited story variations', "Variations d'histoires illimitées")}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                {tr('Instant parameter editing', 'Modification instantanée des paramètres')}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <div className="w-1.5 h-1.5 bg-purple-400 rounded-full" />
                {tr('No more step-by-step forms', 'Fini les formulaires étape par étape')}
              </div>
            </div>
          </div>

          {/* Action button */}
          <button
            onClick={onClose}
            className="w-full mt-6 py-3 px-4 text-sm font-bold text-white rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2"
          >
            <BookOpen className="w-4 h-4" />
            {tr('Continue Creating', 'Continuer à créer')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StandardReminderModal;